import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Film, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";
import { fmtSize, fmtDateTime } from "@/lib/format";
import { useBlobUrl } from "@/components/chat/useBlobUrl";

const CHUNK = 5 * 1024 * 1024;
const MAX = 100 * 1024 * 1024;
const STATUS = { enviando: "Enviando…", processando: "Preparando para todos os celulares…", pronto: "Pronto", erro: "Erro" };

const Player = ({ api, v }) => {
  const { url } = useBlobUrl(api, `/admin/files/${v.file.id}`);
  return url ? <video data-testid={`video-player-${v.id}`} controls playsInline preload="metadata" src={url} className="max-h-56 w-72 max-w-full rounded-md bg-black" />
    : <div className="flex h-40 w-72 max-w-full items-center justify-center rounded-md bg-black/80"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>;
};

export const VideoLibrary = () => {
  const { api } = useOutletContext();
  const [list, setList] = useState(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const ref = useRef(null);
  const load = () => api.get("/admin/videos").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const busy = (list || []).some((v) => v.status === "processando");
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    setError("");
    if (!f) return;
    if (f.size > MAX) { setError(`“${f.name}” tem mais de 100 MB. Para vídeos longos, use o link do YouTube.`); return; }
    setFile(f);
    if (title.trim().length < 2) setTitle(f.name.replace(/\.[^.]+$/, "").slice(0, 80));
  };
  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setError("Escolha um vídeo."); return; }
    setError(""); setProgress(0);
    try {
      const { data: v } = await api.post("/admin/videos/init", { title, name: file.name, size: file.size });
      const n = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < n; i += 1) {
        await api.put(`/admin/videos/${v.id}/chunk/${i}`, file.slice(i * CHUNK, (i + 1) * CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
        setProgress(Math.round(((i + 1) / n) * 100));
      }
      await api.post(`/admin/videos/${v.id}/complete`, { chunks: n });
      toast.success("Vídeo enviado. Ele fica pronto em alguns instantes.");
      setFile(null); setTitle(""); load();
    } catch (err) {
      setError(`Não foi possível enviar: ${err.response ? errMsg(err) : "sem conexão com o servidor."}`);
    }
    setProgress(null);
  };
  const del = async (v) => {
    if (!window.confirm(`Excluir o vídeo “${v.title}”?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); toast.success("Vídeo excluído."); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-8">
      <form onSubmit={upload} className="space-y-3 rounded-md border border-zap-line p-4">
        <h2 className="font-display text-lg font-semibold">Novo vídeo</h2>
        <Input data-testid="video-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título do vídeo" />
        <input ref={ref} type="file" accept="video/*,.mp4,.mov,.m4v,.webm,.3gp,.mkv,.avi" className="hidden" data-testid="video-file-input" onChange={pick} />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" data-testid="video-choose" onClick={() => ref.current.click()} disabled={progress !== null}><Upload className="mr-2 h-4 w-4" />Escolher vídeo</Button>
          {file ? <span data-testid="video-file-name" className="flex min-w-0 items-center gap-1.5 rounded-md bg-zap-mist px-2 py-1 text-sm font-medium"><Film className="h-4 w-4 shrink-0" /><span className="truncate">{file.name}</span> ({fmtSize(file.size)})</span>
            : <span className="text-sm text-muted-foreground">MP4, MOV, WEBM e outros, até 100 MB.</span>}
        </div>
        {progress !== null && <div data-testid="video-progress" className="h-2.5 overflow-hidden rounded bg-zap-line"><div className="h-full bg-zap-green transition-all" style={{ width: `${progress}%` }} /></div>}
        {error && <p data-testid="video-error" className="flex items-start gap-1.5 rounded-md bg-red-50 px-2 py-1.5 text-sm text-red-800"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
        <Button data-testid="video-save" disabled={progress !== null || title.trim().length < 2}>{progress !== null ? `Enviando… ${progress}%` : "Cadastrar vídeo"}</Button>
      </form>
      <ul data-testid="video-list" className="space-y-3">
        {list === null && <li className="text-sm text-muted-foreground">Carregando…</li>}
        {list?.length === 0 && <li className="text-sm text-muted-foreground">Nenhum vídeo cadastrado.</li>}
        {(list || []).map((v) => (
          <li key={v.id} data-testid={`video-${v.id}`} className="flex flex-wrap items-start gap-3 rounded-md border border-zap-line p-3">
            {v.status === "pronto" ? <Player api={api} v={v} /> : <div className="flex h-24 w-40 items-center justify-center rounded-md bg-zap-mist">{v.status === "erro" ? <AlertCircle className="h-6 w-6 text-red-700" /> : <Loader2 className="h-6 w-6 animate-spin text-zap-green" />}</div>}
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-semibold">{v.title}</p>
              <p data-testid={`video-status-${v.id}`} className={`text-sm ${v.status === "erro" ? "text-red-700" : "text-muted-foreground"}`}>{v.status === "erro" ? v.error : STATUS[v.status]}{v.file ? ` · ${fmtSize(v.file.size)}` : ""}</p>
              <p className="text-xs text-muted-foreground">{fmtDateTime(v.created_at)} · {v.created_by}</p>
            </div>
            <button data-testid={`video-delete-${v.id}`} onClick={() => del(v)} aria-label="Excluir vídeo" className="rounded p-2 text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};
